import { Component, createRef, type ReactNode } from "react"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import ApiHandler from "src/api/ApiHandler"
import ErrorHandler from "src/components/errors/ErrorHandler"
import Loader from "src/components/general/Loading"
import Format from "src/utils/Format"
import StateConstants from "src/states/StateContstants"
import AreaSearchForm from "src/components/search/AreaSearchForm"
import AreaMap from "src/components/areas/AreaMap"
import AreaSalesPerYearChart from "src/components/areas/AreaSalesPerYearChart"
import RecentSaleCard from "src/components/areas/RecentSaleCard"
import ComparableHome from "src/components/comparable_homes/ComparableHome"
import "src/components/areas/AreaAnalysisPage.css"
import "src/components/valuations/ValuationPage.css"
import PriceWidget from "src/components/areas/PriceWidget"
import AreaAnalysis from "src/components/areas/areaAnalysisDescription/AreaAnalysis"
import Area from "src/components/areas/areaAnalysisDescription/Area"
import type { AnalysisBuilderChain } from "src/components/areas/areaAnalysisDescription/analysis_builders/AnalysisBuilderChain"
import AreaAnalysisPageSEO from "src/components/areas/AreaAnalysisPageSEO"
import AreaTitleVariantSelector from "src/components/areas/seo/AreaTitleVariantSelector"
import "src/components/comparable_homes/ComparableHome.css"

interface AreaAnalysisPageContentProps {
    areaId: string
    area: any
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    errorHandler: ErrorHandler
    loader: Loader
    areaAnalysisBuilderChain: AnalysisBuilderChain
}

const MAX_RECENT_SALES_TO_ENRICH = 5

function getSalePriceAndDate(sale: any): { sale_price: number; sale_date: string } | null {
    const sh = sale?.sh ?? {}
    const dates = typeof sh === "object" ? Object.keys(sh).sort() : []
    const latestSale = dates.length > 0 ? sh[dates[dates.length - 1]] : null
    const salePrice = latestSale?.p?.v != null ? parseFloat(String(latestSale.p.v)) : null
    const saleDateRaw = latestSale?.d ?? (dates.length > 0 ? dates[dates.length - 1] : null)
    if (salePrice == null || !saleDateRaw) return null
    try {
        const d = new Date(saleDateRaw)
        const saleDate = isNaN(d.getTime()) ? String(saleDateRaw) : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
        return { sale_price: salePrice, sale_date: saleDate }
    } catch {
        return { sale_price: salePrice, sale_date: String(saleDateRaw) }
    }
}

function formatDescriptionWithBoldFigures(text: string): ReactNode[] {
    const pattern = /(€[\d,]+K?|[\d.]+%)/g
    const parts: ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index))
        }
        parts.push(<strong key={match.index} className="area-analysis-figure">{match[0]}</strong>)
        lastIndex = pattern.lastIndex
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex))
    }
    return parts
}

export default class AreaAnalysisPageContent extends Component<AreaAnalysisPageContentProps> {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    recentSalesCarouselRef = createRef<HTMLDivElement>()
    state: { enrichedRecentSales: (null | any | { error: string })[]; hoveredMarkerId: string | null }

    constructor(props: AreaAnalysisPageContentProps) {
        super(props)
        this.stateHandler = props.stateHandler
        this.apiHandler = props.apiHandler
        this.state = { enrichedRecentSales: [], hoveredMarkerId: null }
        this.stateHandler.addListener(StateConstants.MAP_MARKER_HOVER, (id: string | null) => {
            this.setState({ hoveredMarkerId: id ?? null })
        })
        this.stateHandler.addListener(StateConstants.IS_GOOGLE_BOT, (isGoogleBot: boolean) => {
            this.setState({ isGoogleBot: isGoogleBot })
        })
    }

    private buildAnalysisDescription(area: any, analysis: any): string {
        const safeArea = area != null && typeof area === "object" ? (area as Record<string, unknown>) : {}
        const safeAnalysis = analysis != null && typeof analysis === "object" ? (analysis as Record<string, unknown>) : {}

        try {
            const typedAnalysis = AreaAnalysis.fromDict(safeAnalysis)
            const typedArea = Area.fromDict(safeArea)
            return this.props.areaAnalysisBuilderChain.build(typedAnalysis, typedArea).trim()
        } catch {
            return ""
        }
    }

    render() {
        const { area } = this.props
        if (!area) {
            return <div className="area-analysis-empty">No area data</div>
        }

        const name = area.n ?? area.neighbourhood ?? ""
        const region = area.r ?? area.region ?? ""
        const country = area.c ?? area.country ?? ""
        const analysis = area.a ?? {}
        const oneYearMedian = analysis["1ym"]
        const oneYearFirstQuartilePrice = analysis["1yq1"]
        const oneYearThirdQuartilePrice = analysis["1yq3"]
        const oneYearSalesCount = analysis["1ys"]
        const yearOnYearChange = analysis["yoym"] ?? null
        const interQuartileRange = analysis["1yi"]
        const recentSales = analysis["rs"] ?? []
        const centroid = analysis["gc"] ?? area["gc"]
        const description = this.buildAnalysisDescription(area, analysis)
        const isGoogleBot = this.stateHandler.get(StateConstants.IS_GOOGLE_BOT)

        const currencySymbol = oneYearMedian?.s === "EUR" ? "€" : (oneYearMedian?.s ?? "€")

        const center = centroid
            ? { lt: centroid.lt ?? centroid.lattitude, ln: centroid.ln ?? centroid.longitude }
            : { lt: 0, ln: 0 }

        const mapMarkers = recentSales
            .filter((s: any) => s?.gc?.lt != null && s?.gc?.ln != null)
            .map((s: any, i: number) => ({
                id: s?.id ?? s?.gi ?? `sale-${i}`,
                lt: s.gc.lt,
                ln: s.gc.ln,
                address: s?.a ?? s?.address
            }))

        return (
            <div>
                <AreaAnalysisPageSEO
                    areaId={this.props.areaId}
                    area={area}
                    areaAnalysisBuilderChain={this.props.areaAnalysisBuilderChain}
                />

                <div className="house-form-container area-form-container">
                    <div className="area-hero-row">
                        <div className="area-hero-left">
                            <header className="area-analysis-header">
                                <h1 className="area-analysis-title">{AreaTitleVariantSelector.getTitle(this.props.areaId, name, region || "Ireland")}</h1>
                                <p className="area-analysis-subtitle">
                                    {region ? `${region}, ${country}` : country}
                                </p>
                            </header>

                            <PriceWidget
                                medianPrice={oneYearMedian.v}
                                lowPrice={oneYearFirstQuartilePrice?.v != null ? parseFloat(String(oneYearFirstQuartilePrice.v)) : 0}
                                highPrice={oneYearThirdQuartilePrice?.v != null ? parseFloat(String(oneYearThirdQuartilePrice.v)) : 0}
                                currencySymbol={currencySymbol}
                                timeHorizon="12 months"
                                sales={oneYearSalesCount != null ? oneYearSalesCount : undefined}
                                yearOnYearChange={yearOnYearChange}
                                priceSpread={interQuartileRange?.v != null ? (typeof interQuartileRange.v === "number" ? interQuartileRange.v : parseFloat(String(interQuartileRange.v))) : null}
                            />
                        </div>

                        {description ? (
                            <section className="area-analysis-description" aria-label="Area market summary">
                                <h2 className="area-analysis-description-heading">{name} House Price Trends & Market Analysis</h2>
                                <p className="area-analysis-description-text">{formatDescriptionWithBoldFigures(description)}</p>
                            </section>
                        ) : null}
                    </div>

                    <div className="address-container">
                        <div className="widget-header">
                            <h3 className="widget-title">Recent sales in {name}</h3>
                            <p className="widget-subtitle">
                                Showing {recentSales.length} most recent property transactions
                                {region ? ` in ${name}, ${region}` : ` in ${name}`}
                            </p>
                        </div>
                        <div className="recent-sales-content">
                            <div className="area-map-container">
                                <AreaMap
                                    center={center}
                                    markers={mapMarkers}
                                    className="area-map"
                                    stateHandler={this.stateHandler}
                                />
                            </div>
                            <div className="recent-sales-properties">
                                <div className="comparable-homes comparable-homes-area">
                                    <div className="table-container" ref={this.recentSalesCarouselRef}>
                                        {(() => {
                                            const hoveredId = this.state.hoveredMarkerId
                                            const highlightedSale = hoveredId != null ? recentSales.find((s: any) => (s?.id ?? s?.gi) === hoveredId) : null
                                            const orderedSales = highlightedSale
                                                ? [highlightedSale, ...recentSales.filter((s: any) => (s?.id ?? s?.gi) !== hoveredId)]
                                                : recentSales
                                            return orderedSales.map((sale: any, index: number) => {
                                                const saleId = sale?.id ?? sale?.gi ?? null
                                                return (
                                                    <RecentSaleCard
                                                        key={saleId ?? `sale-${index}`}
                                                        sale={sale}
                                                        apiHandler={this.apiHandler}
                                                        isGoogleBot={isGoogleBot}
                                                        isHighlighted={saleId != null && hoveredId === saleId}
                                                        onSelect={saleId != null ? () => {
                                                this.stateHandler.set(StateConstants.MAP_MARKER_HOVER, saleId)
                                                this.recentSalesCarouselRef.current?.scrollTo({ top: 0, behavior: "smooth" })
                                            } : undefined}
                                                    />
                                                )
                                            })
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        )
    }
}
